import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { ArrowLeft, MapPin, Loader2, Zap, Bike, Car, Sparkles, Wallet, Coins, Smartphone, Users, LocateFixed } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import PassengerBottomNav from "../components/PassengerBottomNav";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const CAMEROON_DEFAULT = { lat: 4.0511, lng: 9.7679 };

const VEHICLE_ICONS = { moto: Bike, berline: Car, confort: Sparkles };
const PAYMENT_OPTIONS = [
  { id: "cash", label: "Cash", icon: Wallet },
  { id: "tft", label: "TFT", icon: Coins },
  { id: "mtn", label: "MTN MoMo", icon: Smartphone },
  { id: "orange", label: "Orange Money", icon: Smartphone },
];

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} FCFA`; }

function PlacesAutocomplete({ value, onPick, placeholder, testId, bias }) {
  const inputRef = useRef(null);
  const placesLib = useMapsLibrary("places");
  const [text, setText] = useState(value || "");

  useEffect(() => { setText(value || ""); }, [value]);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    const opts = {
      fields: ["formatted_address", "geometry", "name"],
      componentRestrictions: { country: "cm" },
    };
    if (bias) {
      opts.bounds = new window.google.maps.LatLngBounds(
        { lat: bias.lat - 0.15, lng: bias.lng - 0.15 },
        { lat: bias.lat + 0.15, lng: bias.lng + 0.15 }
      );
      opts.strictBounds = false;
    }
    const auto = new placesLib.Autocomplete(inputRef.current, opts);
    const listener = auto.addListener("place_changed", () => {
      const place = auto.getPlace();
      if (!place?.geometry?.location) return;
      const picked = {
        address: place.formatted_address || place.name,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setText(picked.address);
      onPick(picked);
    });
    return () => { if (listener) window.google.maps.event.removeListener(listener); };
  }, [placesLib, bias, onPick]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent border-none outline-none text-sm font-medium text-[#0A2540] placeholder:text-[#94A3B8]"
      data-testid={testId}
    />
  );
}

function RouteRenderer({ pickup, dropoff, onRoute }) {
  const map = useMap();
  const routesLib = useMapsLibrary("routes");
  const rendererRef = useRef(null);

  useEffect(() => {
    if (!routesLib || !map) return;
    if (!rendererRef.current) {
      rendererRef.current = new routesLib.DirectionsRenderer({
        map, suppressMarkers: true,
        polylineOptions: { strokeColor: "#FACC15", strokeWeight: 5, strokeOpacity: 0.95 },
      });
    }
    if (pickup && dropoff) {
      const svc = new routesLib.DirectionsService();
      svc.route({
        origin: { lat: pickup.lat, lng: pickup.lng },
        destination: { lat: dropoff.lat, lng: dropoff.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      }).then((result) => {
        rendererRef.current.setDirections(result);
        const leg = result.routes[0]?.legs?.[0];
        if (leg) onRoute({ distance_km: leg.distance.value / 1000, duration_min: leg.duration.value / 60 });
      }).catch(() => {});
    }
  }, [routesLib, map, pickup, dropoff, onRoute]);
  return null;
}

function GpsLocateButton({ onLocated, testId, autoOnMount = false }) {
  const geocodingLib = useMapsLibrary("geocoding");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const autoTriedRef = useRef(false);

  const trigger = useCallback(async () => {
    setErr("");
    if (!("geolocation" in navigator)) { setErr("GPS non supporté"); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      let address = `GPS ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      try {
        if (geocodingLib) {
          const geo = new geocodingLib.Geocoder();
          const res = await geo.geocode({ location: { lat, lng } });
          if (res?.results?.[0]) address = res.results[0].formatted_address;
        }
      } catch (_e) { /* keep fallback */ }
      onLocated({ address, lat, lng });
      setLoading(false);
    }, (e) => {
      setLoading(false);
      setErr(e.code === 1 ? "Permission refusée" : "Position introuvable");
    }, { enableHighAccuracy: true, timeout: 12000 });
  }, [geocodingLib, onLocated]);

  // Auto-trigger once when the geocoder is ready — so the passenger doesn't
  // have to press anything for the pickup field to fill in.
  useEffect(() => {
    if (!autoOnMount || autoTriedRef.current || !geocodingLib) return;
    autoTriedRef.current = true;
    trigger();
  }, [autoOnMount, geocodingLib, trigger]);

  return (
    <button
      type="button"
      onClick={trigger}
      disabled={loading}
      className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1.5 rounded-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 disabled:opacity-60 transition"
      data-testid={testId}
      title={err || "Utiliser ma position actuelle"}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <LocateFixed className="w-3 h-3" />}
      <span className="hidden sm:inline">GPS</span>
    </button>
  );
}

function VehicleOption({ q, selected, onSelect }) {
  const Icon = VEHICLE_ICONS[q.id] || Car;
  return (
    <button
      type="button"
      onClick={() => onSelect(q.id)}
      className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 transition-all text-left ${
        selected
          ? "bg-[#0A2540] text-white shadow-md"
          : "bg-white border border-[#E2E8F0] text-[#0A2540] hover:border-[#0A2540]"
      }`}
      data-testid={`express-vehicle-${q.id}`}
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${selected ? "bg-[#FACC15]" : "bg-[#F1F5F9]"}`}>
        <Icon className={`w-5 h-5 ${selected ? "text-[#0A2540]" : "text-[#0A2540]"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-extrabold text-sm" style={{ fontFamily: "Outfit" }}>
          {q.label}
        </div>
        <div className={`text-[11px] truncate ${selected ? "text-white/70" : "text-[#64748B]"}`}>{q.description}</div>
      </div>
      <div className="text-right shrink-0">
        <div className={`font-extrabold text-sm ${selected ? "text-[#FACC15]" : "text-[#0A2540]"}`} style={{ fontFamily: "Outfit" }}>
          {typeof q.price_xaf === "number" ? fmtXAF(q.price_xaf) : "—"}
        </div>
      </div>
    </button>
  );
}

function SearchingOverlay({ vehicle, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#0A2540]/95 backdrop-blur-sm flex flex-col items-center justify-center px-6" data-testid="express-searching-overlay">
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-[#FACC15]/30 animate-ping" />
        <div className="absolute inset-0 rounded-full bg-[#FACC15]/20 animate-pulse" style={{ animationDelay: "0.3s" }} />
        <div className="relative w-32 h-32 rounded-full bg-[#FACC15] flex items-center justify-center">
          <Zap className="w-12 h-12 text-[#0A2540]" strokeWidth={2.5} />
        </div>
      </div>
      <p className="mt-8 text-[11px] uppercase tracking-[0.3em] font-bold text-[#FACC15]">Trigo Express</p>
      <h2 className="mt-2 text-2xl font-extrabold text-white text-center" style={{ fontFamily: "Outfit" }}>
        Recherche d&apos;un chauffeur…
      </h2>
      <p className="mt-2 text-sm text-white/70 text-center max-w-xs">
        Nous cherchons le meilleur chauffeur {vehicle || ""} près de vous. Merci de patienter quelques secondes.
      </p>
      <button
        type="button"
        onClick={onCancel}
        className="mt-8 px-6 h-11 rounded-full border border-white/25 text-white/90 text-sm font-semibold hover:bg-white/10 transition"
        data-testid="express-cancel-search"
      >
        Annuler la recherche
      </button>
    </div>
  );
}

function ExpressInner() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [route, setRoute] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [nearbyDrivers, setNearbyDrivers] = useState(0);
  const [nearbyDriversList, setNearbyDriversList] = useState([]);
  const [selectedType, setSelectedType] = useState("moto");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  // NEW pricing modes: "forfait" (time-based flat) or "trip" (auto-zone by distance)
  const [pricingMode, setPricingMode] = useState("trip");
  const [selectedForfait, setSelectedForfait] = useState("standard");

  useEffect(() => {
    api.get("/express/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (pickup || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setPickup({ address: "Ma position actuelle", lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 5000 }
    );
  }, [pickup]);

  // Iter 78 — fetch nearby online Express drivers around the pickup
  // point so we can render them as green car markers on the mini-map.
  // Refreshes every 15 s while the user is composing their order.
  useEffect(() => {
    if (!pickup?.lat || !pickup?.lng) { setNearbyDriversList([]); return; }
    let cancelled = false;
    const run = async () => {
      try {
        const { data } = await api.get("/express/nearby-drivers", {
          params: { lat: pickup.lat, lng: pickup.lng, radius_km: 5 },
        });
        if (!cancelled) setNearbyDriversList(data.drivers || []);
      } catch { /* silent */ }
    };
    run();
    const id = setInterval(run, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pickup?.lat, pickup?.lng]);

  useEffect(() => {
    if (!route || !pickup) { setQuotes([]); setNearbyDrivers(0); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/express/quote", {
          distance_km: route.distance_km,
          duration_min: route.duration_min,
          pickup_lat: pickup.lat,
          pickup_lng: pickup.lng,
        });
        setQuotes(data.quotes || []);
        setNearbyDrivers(data.nearby_drivers || 0);
      } catch (_e) { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [route, pickup]);

  const selectedQuote = useMemo(() => quotes.find((q) => q.id === selectedType) || quotes[0], [quotes, selectedType]);

  // Live price for the new pricing modes (forfait / trip)
  const forfaits = config?.forfaits || [];
  const tripZones = config?.trip_zones || [];
  // Service options can be individually toggled by the admin. When one is
  // disabled we hide its tab and, if we happened to be on it, we
  // automatically switch to the other (kept in an effect below).
  const options = config?.options || { trip_simple_enabled: true, forfait_enabled: true };
  const tripEnabled    = options.trip_simple_enabled !== false;
  const forfaitEnabled = options.forfait_enabled     !== false;
  // Iter 74 — Automatic Trigo Berline fallback tab (distance-based classic
  // VTC pricing). Kicks in when either "Trajet simple" or "Forfait temps"
  // is temporarily or permanently off. Backend sends the master flag +
  // per-service "next active at" HH:MM strings so we can decorate the tabs.
  const berlineFallback     = !!options.berline_fallback_active;
  const tripNextActiveAt    = options.trip_simple_next_active_at || "";
  const forfaitNextActiveAt = options.forfait_next_active_at     || "";
  const berlineType         = useMemo(
    () => (config?.vehicle_types || []).find((t) => t.id === "berline") || (config?.vehicle_types || [])[0] || null,
    [config?.vehicle_types],
  );

  useEffect(() => {
    if (!config) return;
    // Prefer whatever mode remains available. When BOTH main modes are off
    // we snap to the berline fallback so the passenger always has a way
    // to book — no more "service suspended" dead-end.
    if (pricingMode === "trip" && !tripEnabled) {
      setPricingMode(forfaitEnabled ? "forfait" : "berline");
    } else if (pricingMode === "forfait" && !forfaitEnabled) {
      setPricingMode(tripEnabled ? "trip" : "berline");
    } else if (pricingMode === "berline" && tripEnabled && forfaitEnabled) {
      // Berline was the fallback; the admin re-enabled the standard modes.
      setPricingMode("trip");
    }
  }, [config, tripEnabled, forfaitEnabled, pricingMode]);
  const forfaitDoc = useMemo(() => forfaits.find((f) => f.id === selectedForfait), [forfaits, selectedForfait]);
  const tripZoneDoc = useMemo(() => {
    if (!route) return null;
    const km = route.distance_km || 0;
    const ordered = [...tripZones].sort((a, b) => (a.max_km ?? 9999) - (b.max_km ?? 9999));
    return ordered.find((z) => z.max_km == null || km <= z.max_km) || ordered[ordered.length - 1] || null;
  }, [tripZones, route]);

  // Berline distance-based price (mirrors the backend `_compute_express_price`).
  const berlinePrice = useMemo(() => {
    if (!berlineType || !route) return 0;
    const base = Number(berlineType.base || 0);
    const perKm = Number(berlineType.per_km || 0);
    const perMin = Number(berlineType.per_min || 0);
    const minFare = Number(berlineType.min_fare || 0);
    const surge = Number(config?.surge_multiplier || 1);
    const raw = (base + perKm * (route.distance_km || 0) + perMin * (route.duration_min || 0)) * surge;
    return Math.max(minFare, Math.round(raw));
  }, [berlineType, route, config?.surge_multiplier]);

  const finalPrice = useMemo(() => {
    if (pricingMode === "forfait") return forfaitDoc?.price || 0;
    if (pricingMode === "trip")    return tripZoneDoc?.price || 0;
    if (pricingMode === "berline") return berlinePrice || 0;
    return selectedQuote?.price_xaf || 0;
  }, [pricingMode, forfaitDoc, tripZoneDoc, berlinePrice, selectedQuote]);

  const canOrder = user?.role === "passenger"
    && (tripEnabled || forfaitEnabled || berlineFallback)
    && pickup
    && (pricingMode === "forfait" || dropoff)
    && (pricingMode === "forfait" || route)
    && (
      pricingMode === "forfait" ? !!forfaitDoc
      : pricingMode === "berline" ? (!!berlineType && berlinePrice > 0)
      : !!tripZoneDoc
    )
    && !submitting;

  const handleOrder = async () => {
    if (!canOrder) {
      if (!user) { navigate("/login"); return; }
      if (user.role !== "passenger") { toast.error("Seuls les passagers peuvent commander une course."); return; }
      return;
    }
    setSubmitting(true);
    try {
      const commonPayload = {
        vehicle_type: pricingMode === "berline" ? "berline" : (selectedType || "berline"),
        pickup_address: pickup.address,
        pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        payment_method: paymentMethod,
      };
      const modePayload = pricingMode === "forfait"
        ? {
            pricing_mode: "forfait",
            forfait_id: forfaitDoc.id,
            dropoff_address: dropoff?.address || "",
            dropoff_lat: dropoff?.lat || null,
            dropoff_lng: dropoff?.lng || null,
            distance_km: route?.distance_km || 0,
            duration_min: route?.duration_min || (forfaitDoc.duration_min || 0),
          }
        : pricingMode === "berline"
        ? {
            // Iter 74 — Fallback classic distance-based pricing.
            pricing_mode: "distance",
            dropoff_address: dropoff.address,
            dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
            distance_km: route.distance_km,
            duration_min: route.duration_min,
          }
        : {
            pricing_mode: "trip",
            dropoff_address: dropoff.address,
            dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
            distance_km: route.distance_km,
            duration_min: route.duration_min,
          };
      const { data } = await api.post("/express/request", { ...commonPayload, ...modePayload });
      toast.success("Course commandée !", {
        description: `Chauffeur assigné : ${data.driver?.name || "en attente"} · ETA ${data.driver?.eta_min || "?"} min`,
        duration: 8000,
      });
      navigate("/passenger?tab=express-bookings");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la commande");
      setSubmitting(false);
    }
  };

  const center = pickup || dropoff || CAMEROON_DEFAULT;

  return (
    <div className="min-h-screen bg-[#F5F7FA] pb-24 sm:pb-6" style={{ fontFamily: "Outfit, Inter, sans-serif" }} data-testid="express-page">
      {submitting && <SearchingOverlay vehicle={selectedQuote?.label} onCancel={() => setSubmitting(false)} />}
      <div className="max-w-lg md:max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        {/* Back + title */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-[#E2E8F0] text-[#0A2540] hover:bg-[#0A2540] hover:text-white transition"
            data-testid="express-back-btn"
            aria-label="Retour"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FACC15]">Trigo Express</p>
            <h1 className="text-xl sm:text-2xl font-extrabold text-[#0A2540] leading-tight" style={{ fontFamily: "Outfit" }}>
              Où allons-nous ?
            </h1>
          </div>
        </div>

        {/* Map card */}
        <div className="relative h-[220px] sm:h-[280px] md:h-[320px] rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-sm mb-4 bg-[#F1F5F9]" data-testid="express-map">
          <Map
            defaultCenter={center}
            defaultZoom={14}
            center={pickup && dropoff ? undefined : center}
            gestureHandling="greedy"
            disableDefaultUI
            mapId="trigo-express-map"
            style={{ width: "100%", height: "100%" }}
          >
            {pickup && (
              <AdvancedMarker position={pickup}>
                <div className="w-5 h-5 rounded-full bg-[#0A2540] border-2 border-white shadow-lg" />
              </AdvancedMarker>
            )}
            {dropoff && (
              <AdvancedMarker position={dropoff}>
                <div className="w-5 h-5 rounded-full bg-[#FACC15] border-2 border-white shadow-lg" />
              </AdvancedMarker>
            )}
            {/* Iter 78 — nearby online drivers (green car dots). */}
            {nearbyDriversList.map((d) => (
              <AdvancedMarker
                key={d.driver_id}
                position={{ lat: d.lat, lng: d.lng }}
                title={`${d.driver_name}${d.vehicle ? " · " + d.vehicle : ""} · ${d.distance_km} km`}
              >
                <div
                  className="w-7 h-7 rounded-full bg-[#10B981] border-2 border-white shadow-md flex items-center justify-center text-white"
                  style={{ fontSize: 14 }}
                  data-testid={`express-driver-marker-${d.driver_id}`}
                >🚗</div>
              </AdvancedMarker>
            ))}
            <RouteRenderer pickup={pickup} dropoff={dropoff} onRoute={setRoute} />
          </Map>
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-[#0A2540] text-[11px] font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border border-[#E2E8F0]" data-testid="express-nearby-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
            {(() => {
              const n = Math.max(nearbyDrivers, nearbyDriversList.length);
              return n > 0 ? `${n} chauffeur${n > 1 ? "s" : ""} à proximité` : "Recherche en cours…";
            })()}
          </div>
        </div>

        {/* A → B card */}
        <div className="bg-white rounded-3xl border border-[#E2E8F0] mb-4 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[#F1F5F9]">
            <span className="w-6 h-6 rounded-full bg-[#0A2540] text-white text-[11px] font-extrabold flex items-center justify-center shrink-0">A</span>
            <div className="flex-1 min-w-0">
              <PlacesAutocomplete value={pickup?.address || ""} onPick={setPickup} placeholder="Point de départ…" testId="express-pickup-input" />
            </div>
            <GpsLocateButton onLocated={setPickup} testId="express-pickup-gps-btn" autoOnMount={!pickup?.lat} />
          </div>
          <div className="flex items-center gap-3 px-4 py-3.5">
            <span className="w-6 h-6 rounded-full bg-[#FACC15] text-[#0A2540] text-[11px] font-extrabold flex items-center justify-center shrink-0">B</span>
            <div className="flex-1 min-w-0">
              <PlacesAutocomplete value={dropoff?.address || ""} onPick={setDropoff}
                placeholder={pricingMode === "forfait" ? "Destination (optionnel pour un forfait)" : "Entrez la destination…"}
                bias={pickup} testId="express-dropoff-input" />
            </div>
          </div>
        </div>

        {/* Pricing mode tabs (NEW). Each tab is only rendered when the
            corresponding option is enabled in the admin Express config.
            If a single option is active we drop the tab strip entirely to
            avoid a lonely tab pretending there's a choice. */}
        {/* Pricing mode tabs. Iter 74:
             - We NEVER show a "service suspended" dead-end anymore.
             - When a service is off (manual OR schedule), its tab is
               replaced by the Trigo Berline fallback tab (classic
               distance-based VTC pricing on the berline vehicle_type).
             - A small "Revient à HH:MM" badge is displayed next to any
               tab that's temporarily off due to a schedule. */}
        {(() => {
          const modes = [];
          if (tripEnabled) {
            modes.push({ id: "trip", label: "🛣️ Trajet simple" });
          } else if (berlineFallback) {
            modes.push({
              id: "berline",
              label: "🚗 Trigo Berline",
              hint: tripNextActiveAt ? `Revient à ${tripNextActiveAt}` : "",
            });
          }
          if (forfaitEnabled) {
            modes.push({ id: "forfait", label: "⏱️ Forfait temps" });
          } else if (berlineFallback && !modes.some((m) => m.id === "berline")) {
            modes.push({
              id: "berline",
              label: "🚗 Trigo Berline",
              hint: forfaitNextActiveAt ? `Revient à ${forfaitNextActiveAt}` : "",
            });
          }
          if (modes.length === 0) return null;
          if (modes.length === 1) {
            const m = modes[0];
            return (
              <div className="bg-white rounded-2xl border border-[#E2E8F0] px-4 py-3 mb-4 text-center" data-testid="express-mode-single">
                <p className="text-sm font-bold text-[#0A2540]">{m.label}</p>
                {m.hint && (
                  <p className="text-[10px] text-[#F2A93B] font-semibold mt-0.5" data-testid={`express-mode-hint-${m.id}`}>
                    ⏰ {m.hint}
                  </p>
                )}
              </div>
            );
          }
          return (
            <div className="grid grid-cols-2 bg-white rounded-2xl border border-[#E2E8F0] p-1 mb-4" data-testid="express-mode-tabs">
              {modes.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setPricingMode(m.id)}
                  className={`h-12 rounded-xl text-sm font-bold transition ${pricingMode === m.id ? "bg-[#0A2540] text-[#FACC15]" : "text-[#0A2540] hover:bg-[#F1F5F9]"}`}
                  data-testid={`express-mode-${m.id}`}
                >
                  <span className="block leading-tight">{m.label}</span>
                  {m.hint && (
                    <span className={`block text-[10px] font-medium mt-0.5 ${pricingMode === m.id ? "text-[#FACC15]/80" : "text-[#F2A93B]"}`} data-testid={`express-mode-hint-${m.id}`}>
                      ⏰ {m.hint}
                    </span>
                  )}
                </button>
              ))}
            </div>
          );
        })()}

        {(tripEnabled || forfaitEnabled || berlineFallback) && (
          pricingMode === "berline" ? (
          <>
            <p className="text-xs uppercase tracking-wider font-bold text-[#64748B] mb-2 ml-1">Trigo Berline · tarif à la course</p>
            <div className="p-3 rounded-2xl border border-[#FACC15] bg-[#FEFCE8] mb-4" data-testid="express-berline-summary">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🚗</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#0A2540]">{berlineType?.label || "Trigo Berline"}</p>
                  <p className="text-[11px] text-[#64748B] truncate">{berlineType?.description || "Jusqu'à 4 passagers · climatisée"}</p>
                  {route && (
                    <p className="text-[11px] text-[#64748B]">
                      {route.distance_km?.toFixed(1)} km · ~{Math.round(route.duration_min || 0)} min
                    </p>
                  )}
                </div>
                <p className="text-lg font-extrabold text-[#0A2540] shrink-0" style={{ fontFamily: "Outfit" }}>
                  {route ? fmtXAF(berlinePrice) : "—"}
                </p>
              </div>
            </div>
            {!route && <p className="text-[11px] text-[#64748B] text-center py-2">Saisissez la destination pour calculer le tarif</p>}
          </>
          ) : pricingMode === "trip" ? (
          <>
            <p className="text-xs uppercase tracking-wider font-bold text-[#64748B] mb-2 ml-1">Trajet simple · zone auto-détectée</p>
            <div className="flex flex-col gap-2 mb-4">
              {tripZones.map((z) => {
                const isMatch = tripZoneDoc?.id === z.id;
                const km = route?.distance_km || 0;
                const range = z.max_km == null ? "> 5 km" : `≤ ${z.max_km} km`;
                return (
                  <div key={z.id} className={`p-3 rounded-2xl border ${isMatch ? "border-[#FACC15] bg-[#FEFCE8]" : "border-[#E2E8F0] bg-white opacity-60"}`} data-testid={`express-zone-${z.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xl shrink-0">{z.emoji || "🛣️"}</span>
                        <div className="min-w-0">
                          <p className="font-bold text-[#0A2540] truncate">{z.label}</p>
                          <p className="text-[11px] text-[#64748B] truncate">{range}{isMatch && route ? ` · votre trajet ${km.toFixed(1)} km` : ""}</p>
                        </div>
                      </div>
                      <p className="text-lg font-extrabold text-[#0A2540] shrink-0" style={{ fontFamily: "Outfit" }}>{fmtXAF(z.price)}</p>
                    </div>
                  </div>
                );
              })}
              {!route && <p className="text-[11px] text-[#64748B] text-center py-2">Saisissez la destination pour détecter la zone</p>}
            </div>
          </>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wider font-bold text-[#64748B] mb-2 ml-1">Forfait temps · kilométrage libre</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {forfaits.map((f) => {
                const active = selectedForfait === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setSelectedForfait(f.id)}
                    className={`p-3 rounded-2xl text-left transition ${active ? "bg-[#0A2540] text-white border-2 border-[#FACC15]" : "bg-white border border-[#E2E8F0] text-[#0A2540] hover:border-[#0A2540]"}`}
                    data-testid={`express-forfait-${f.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{f.emoji || "⏱️"}</span>
                      <p className="text-sm font-extrabold">{f.label}</p>
                    </div>
                    <p className={`text-[11px] ${active ? "text-[#FACC15]/90" : "text-[#64748B]"}`}>{Math.floor(f.duration_min / 60)}h{f.duration_min % 60 ? ` ${f.duration_min % 60}min` : ""}</p>
                    <p className={`text-lg font-extrabold mt-1 ${active ? "text-[#FACC15]" : "text-[#0A2540]"}`} style={{ fontFamily: "Outfit" }}>{fmtXAF(f.price)}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#64748B] mb-3 px-1">
              💡 Dépassement : 1 000 F pour les 15 premières minutes, 900 F pour les 15 min suivantes, puis 800 F par tranche de 15 min.
            </p>
          </>
        ))}

        {/* Payment method */}
        <p className="text-xs uppercase tracking-wider font-bold text-[#64748B] mb-2 ml-1">Paiement</p>
        <div className="grid grid-cols-4 gap-2 mb-4" data-testid="express-payment-methods">
          {PAYMENT_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = paymentMethod === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setPaymentMethod(opt.id)}
                className={`flex flex-col items-center gap-1 py-3 rounded-2xl transition-all ${
                  active ? "bg-[#0A2540] text-white shadow" : "bg-white border border-[#E2E8F0] text-[#0A2540] hover:border-[#0A2540]"
                }`}
                data-testid={`express-pay-${opt.id}`}
              >
                <Icon className={`w-4 h-4 ${active ? "text-[#FACC15]" : ""}`} />
                <span className="text-[10px] font-bold">{opt.label}</span>
              </button>
            );
          })}
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4 mb-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#64748B]">
              {pricingMode === "forfait" ? "Forfait choisi" : "Estimation"}
            </p>
            <p className="text-xs text-[#0A2540] font-semibold">
              {pricingMode === "forfait"
                ? (forfaitDoc ? `${forfaitDoc.label} · ${forfaitDoc.duration_min} min` : "—")
                : (route ? `${route.distance_km.toFixed(1)} km · ~${Math.round(route.duration_min)} min` : "Saisissez la destination")}
            </p>
          </div>
          <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }} data-testid="express-total-price">
            {finalPrice ? fmtXAF(finalPrice) : "—"}
          </p>
        </div>

        {/* CTA */}
        <button
          onClick={handleOrder}
          disabled={!canOrder}
          className="w-full h-14 rounded-full bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] font-extrabold text-base shadow-lg shadow-[#FACC15]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
          style={{ fontFamily: "Outfit" }}
          data-testid="express-order-btn"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Recherche…</>
          ) : (
            <>Commander · {finalPrice ? fmtXAF(finalPrice) : "…"}</>
          )}
        </button>

        <div className="flex justify-between items-center mt-3 text-[11px] text-[#64748B]">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Paiement à la course</span>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Cameroun</span>
        </div>
      </div>
      {user?.role === "passenger" && <PassengerBottomNav />}
    </div>
  );
}

export default function ExpressPage() {
  if (!MAPS_KEY) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] text-[#0A2540] p-6 text-center">
        <div>
          <p className="text-lg font-bold mb-2" style={{ fontFamily: "Outfit" }}>Trigo Express indisponible</p>
          <p className="text-sm text-[#64748B]">La clé Google Maps est absente. Contactez le support.</p>
        </div>
      </div>
    );
  }
  return (
    <APIProvider apiKey={MAPS_KEY} libraries={["places", "routes", "marker", "geocoding"]}>
      <ExpressInner />
    </APIProvider>
  );
}
